import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { CertificationsList } from '@/features/certifiers/components/CertificationsList';
import { EnableNotifications } from '@/features/notifications';
import { isProvider } from '@/features/profile';
import { formatDualDate } from '@/shared/lib/dates';
import { roleLabel } from '@/shared/lib/labels';
import { ArchHeader } from '@/shared/ui/ArchHeader';
import { Async } from '@/shared/ui/Async';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog';
import { Stars } from '@/shared/ui/Stars';
import { useUiStore } from '@/stores/ui.store';

import { useSignOut } from '../../auth/hooks/use-auth';
import { useExportMyData, useMyProfile, useRequestDeletion } from '../hooks/use-profile';

/** Profile summary + settings (language, theme, edit, sign out). */
export function ProfileRoute(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const query = useMyProfile();
  const locale = useUiStore((s) => s.locale);
  const toggleLocale = useUiStore((s) => s.toggleLocale);
  const toggleTheme = useUiStore((s) => s.toggleTheme);
  const signOut = useSignOut();
  const exportData = useExportMyData();
  const requestDeletion = useRequestDeletion();
  const [confirmDelete, setConfirmDelete] = useState(false);

  function downloadData(): void {
    exportData.mutate(undefined, {
      onSuccess: (data) => {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'badeel-my-data.json';
        a.click();
        URL.revokeObjectURL(url);
      },
    });
  }

  return (
    <Async query={query}>
      {(profile) =>
        profile ? (
          <>
            {/* Arch mark uses the Arabic letter ب (baa) as the brand initial —
                consistent with LoginRoute and the handoff's "letter-in-circle"
                mark treatment. Emoji placeholders removed per P6-4. */}
            <ArchHeader mark="ب" />
            <main className="page">
              <div className="stack">
                <div style={{ textAlign: 'center' }}>
                  <h1>{profile.fullName}</h1>
                  <p className="muted">{roleLabel(t, profile.role)}</p>
                  {isProvider(profile) ? (
                    <div style={{ marginTop: 'var(--space-2)' }}>
                      <Badge
                        label={profile.verified ? t('profile.verified') : t('profile.unverified')}
                        color={
                          profile.verified
                            ? 'var(--color-success)'
                            : 'var(--color-on-surface-muted)'
                        }
                      />
                    </div>
                  ) : null}
                </div>

                {isProvider(profile) ? (
                  <>
                    <Stars value={profile.ratingAvg} count={profile.ratingCount} />
                    <span className="muted">
                      {t('profile.reliability', {
                        pct: Math.round(profile.reliabilityScore * 100),
                      })}
                    </span>
                    <CertificationsList freelancerId={profile.id} />
                  </>
                ) : null}
                {profile.city ? <p>{profile.city}</p> : null}
                {profile.yearsExperience != null ? (
                  <p>{t('profile.yearsValue', { count: profile.yearsExperience })}</p>
                ) : null}
                <p className="muted">
                  {t('profile.memberSince', {
                    date: formatDualDate(new Date(profile.createdAt), locale),
                  })}
                </p>
                {profile.bio ? <p>{profile.bio}</p> : null}
                {profile.qualifications ? (
                  <p>
                    <strong>{t('profile.qualifications')}: </strong>
                    {profile.qualifications}
                  </p>
                ) : null}

                <div className="row" style={{ gap: 'var(--space-2)' }}>
                  <Button variant="outline" onClick={() => navigate('/profile/edit')}>
                    {t('profile.edit')}
                  </Button>
                  <Button variant="outline" onClick={() => navigate('/notifications')}>
                    {t('notifications.title')}
                  </Button>
                  <Button variant="outline" onClick={toggleLocale}>
                    {t('common.changeLanguage')}
                  </Button>
                  <Button variant="outline" onClick={toggleTheme}>
                    {t('common.toggleTheme')}
                  </Button>
                  {isProvider(profile) ? (
                    <Button variant="outline" onClick={() => navigate('/credentials')}>
                      {t('credentials.title')}
                    </Button>
                  ) : null}
                  <Button variant="outline" loading={exportData.isPending} onClick={downloadData}>
                    {t('pdpl.download')}
                  </Button>
                </div>

                <EnableNotifications />

                <div className="row" style={{ gap: 'var(--space-2)' }}>
                  <Button
                    variant="danger"
                    loading={signOut.isPending}
                    onClick={() => signOut.mutate()}
                  >
                    {t('profile.signOut')}
                  </Button>
                  <Button variant="danger" onClick={() => setConfirmDelete(true)}>
                    {t('pdpl.deleteRequest')}
                  </Button>
                </div>

                <ConfirmDialog
                  open={confirmDelete}
                  body={t('pdpl.deleteConfirm')}
                  confirmLabel={t('pdpl.deleteRequest')}
                  loading={requestDeletion.isPending}
                  danger
                  onCancel={() => setConfirmDelete(false)}
                  onConfirm={() =>
                    requestDeletion.mutate(undefined, { onSuccess: () => setConfirmDelete(false) })
                  }
                />
              </div>
            </main>
          </>
        ) : (
          <main className="page">
            <p className="muted">{t('common.empty')}</p>
          </main>
        )
      }
    </Async>
  );
}
